import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { trackPageView } from "./lib/analytics";
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import AuditWizard from "./pages/AuditWizard";
import ScorePreview from "./pages/ScorePreview";
import ResultsDashboard from "./pages/ResultsDashboard";
import BookingPage from "./pages/BookingPage";
import UserDashboard from "./pages/UserDashboard";
import AdminPanel from "./pages/AdminPanel";
import OpsPanel from "./pages/OpsPanel";
import CaseStudyPage from "./pages/CaseStudyPage";
import PrivacyPage from "./pages/PrivacyPage";
import PaymentSuccess from "./pages/PaymentSuccess";
import ContactPage from "./pages/ContactPage";
import InvitePage from "./pages/InvitePage";
import TeamPage from "./pages/TeamPage";
import TeamInvitePage from "./pages/TeamInvitePage";
import CookieConsent from "./components/CookieConsent";
import InsightsPage from "./pages/InsightsPage";
import InsightsArticle from "./pages/InsightsArticle";
import BlueprintInvitePage from "./pages/BlueprintInvitePage";
import { Loader2 } from "lucide-react";

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#0033CC]" /></div>;
  if (!user) return <Navigate to="/auth" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname, document.title);
  }, [location]);
  return null;
}

function AppRoutes() {
  return (
    <>
      <PageViewTracker />
      <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/client-success/:slug" element={<CaseStudyPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/insights" element={<InsightsPage />} />
      <Route path="/insights/:slug" element={<InsightsArticle />} />
      <Route path="/blueprint-invite/:token" element={<BlueprintInvitePage />} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><UserDashboard /></ProtectedRoute>} />
      <Route path="/blueprint" element={<ProtectedRoute><AuditWizard /></ProtectedRoute>} />
      <Route path="/audit" element={<Navigate to="/blueprint" replace />} />
      <Route path="/score-preview/:auditId" element={<ProtectedRoute><ScorePreview /></ProtectedRoute>} />
      <Route path="/results/:auditId" element={<ProtectedRoute><ResultsDashboard /></ProtectedRoute>} />
      <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/booking/:auditId" element={<ProtectedRoute><BookingPage /></ProtectedRoute>} />
      <Route path="/booking" element={<BookingPage />} />
      <Route path="/team" element={<ProtectedRoute><TeamPage /></ProtectedRoute>} />
      <Route path="/team-invite/:token" element={<ProtectedRoute><TeamInvitePage /></ProtectedRoute>} />
      <Route path="/ops" element={<ProtectedRoute adminOnly><OpsPanel /></ProtectedRoute>} />
      <Route path="/admin" element={<Navigate to="/ops" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster />
        <CookieConsent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
